import type React from 'react';

import { cn } from '@cobliteam/ui/utils';
import type { Column } from '@tanstack/react-table';

import { Analytics } from '@cobliteam/analytics';

import Icon from '@cobliteam/ui/icons';

import Button from '@cobliteam/ui/components/Button';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Typography from '@cobliteam/ui/components/Typography';

export interface DataTableHeaderProps<T> {
  title?: string;
  align?: 'left' | 'center' | 'right';
  column: Column<T>;
  children?: React.ReactNode;
}

const DataTableHeader = <T,>({ title, column, align = 'left', children }: DataTableHeaderProps<T>) => {
  const showButton = column.getCanHide() || column.getCanSort();
  const alignHeader = column.columnDef.meta?.alignHeader || align;
  const titleHeader = title || column.columnDef.meta?.name;

  const alignHeaderClass = {
    left: 'flex-start start',
    center: 'm-auto table text-center',
    right: 'flex-end text-right',
  }[alignHeader];

  if (!showButton)
    return (
      <div className={cn('p-2', alignHeaderClass)}>
        <Typography color='secondary' variant='description-title'>
          {titleHeader}
        </Typography>
        {children}
      </div>
    );

  return (
    <div className={cn(alignHeaderClass, 'flex min-h-14 items-center py-2')}>
      <DropdownMenu>
        <div className={cn('flex items-center gap-0.5')}>
          <DropdownMenu.Trigger asChild>
            <Button
              variant='text'
              size='condensed'
              color='default'
              className={cn(
                'flex h-full w-full min-w-32 items-center justify-between whitespace-normal text-wrap p-2 text-left font-medium text-gray-dark leading-120 hover:bg-gray-lightest data-[state=open]:bg-gray-lightest',
                alignHeaderClass
              )}
            >
              {titleHeader}
              {column.getIsSorted() === 'desc' ? (
                <Icon name='arrow-down' size='sm' className='-mt-0.5 ml-1' />
              ) : column.getIsSorted() === 'asc' ? (
                <Icon name='arrow-up' className='-mt-0.5 ml-1' />
              ) : column.getCanSort() ? (
                <Icon name='chevrons-up-down' className='-mt-0.5 ml-1' />
              ) : null}
            </Button>
          </DropdownMenu.Trigger>
          {children}
        </div>
        <DropdownMenu.Content align='start'>
          {column.getCanSort() ? (
            <>
              <DropdownMenu.Item onClick={() => column.clearSorting()} disabled={!!column.getIsSorted() === false}>
                <DropdownMenu.Icon icon={<Icon name='chevrons-up-down' />} />
                <DropdownMenu.Label>Limpar ordenação</DropdownMenu.Label>
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => column.toggleSorting(false)}>
                <DropdownMenu.Icon icon={<Icon name='arrow-up' />} />
                <DropdownMenu.Label>Crescente</DropdownMenu.Label>
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => column.toggleSorting(true)}>
                <DropdownMenu.Icon icon={<Icon name='arrow-down' />} />
                <DropdownMenu.Label>Decrescente</DropdownMenu.Label>
              </DropdownMenu.Item>
              {column.getCanHide() ? <DropdownMenu.Separator /> : null}
            </>
          ) : null}
          {column.getCanPin() ? (
            <>
              <DropdownMenu.Item
                onClick={() => {
                  Analytics.track('DataTable Column Pinned', {
                    column: column.columnDef.meta?.name,
                    pinned: !column.getIsPinned(),
                  });
                  column.pin(column.getIsPinned() ? false : 'left');
                }}
              >
                <DropdownMenu.Icon icon={<Icon name='pin' />} />
                <DropdownMenu.Label>{column.getIsPinned() ? 'Desafixar Coluna' : 'Fixar Coluna'}</DropdownMenu.Label>
              </DropdownMenu.Item>
            </>
          ) : null}
          {column.getCanHide() ? (
            <>
              <DropdownMenu.Item onClick={() => column.toggleVisibility(false)}>
                <DropdownMenu.Icon icon={<Icon name='eye-off' />} />
                <DropdownMenu.Label>Remover Coluna</DropdownMenu.Label>
              </DropdownMenu.Item>
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  );
};

export default DataTableHeader;
